import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// Scroll-to-top ref is used below in step-change effect
import { DRAFT_STORAGE_PREFIX } from '../../constants/forms';
import questionnaire from '../../config/questionnaire';
import {
  ChartBarIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AnchorIcon,
  TrendingUpIcon,
  ShipIcon,
  TargetIcon,
} from '../icons';
import { LikertScale, BinaryChoice, SelectChoice, OpenText } from '../ui/LikertScale';
import NavieraDiagnostics, {
  createEmptyNavieraDiagnostic,
  normalizeDiagnosticList,
  validateNavieraDiagnostics,
} from '../ui/NavieraDiagnostics';
import PreviewStep from './PreviewStep';
import {
  firebaseEnabled,
  getQuestionnaireDraft,
  saveQuestionnaireDraft,
  deleteQuestionnaireDraft,
} from '../../services/firebase';

const DRAFT_AUTOSAVE_DELAY_MS = 1200;

const getDraftOwnerId = (user = {}) => (
  user.uid || user.id || user.email || user.contactEmail || 'usuario'
).toString();

const getSafeDraftSegment = (value) => value.toString().replace(/[/?#[\]]/g, '_');

const buildQuestionnaireDraftId = (user, period) => (
  `${getSafeDraftSegment(getDraftOwnerId(user))}_${getSafeDraftSegment(period || 'current')}`
);

const getDraftTimestamp = (draft) => {
  const parsed = Date.parse(draft?.clientUpdatedAt || draft?.savedAt || draft?.updatedAt || '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

const pickNewestDraft = (...drafts) => (
  drafts.filter(Boolean).sort((a, b) => getDraftTimestamp(b) - getDraftTimestamp(a))[0] || null
);

const normalizeDraftStep = (rawStep, maxStep) => {
  const parsed = Number.parseInt(rawStep, 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(parsed, maxStep));
};

const formatDraftSavedAt = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
};

const dimensionIcons = {
  availability: AnchorIcon,
  costs: TrendingUpIcon,
  reliability: CheckCircleIcon,
  port: ShipIcon,
  general: TargetIcon,
};

const QuestionnaireStepper = ({ currentUser, currentMonth, deadline, onSubmit, onNotify, existingSubmission, onCancelEdit, canSubmit }) => {
  const [step, setStep] = useState(0); // 0-4 para dimensiones, 5 para preview
  const [formData, setFormData] = useState(existingSubmission?.data || {});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftSaveState, setDraftSaveState] = useState({ status: 'idle', savedAt: '' });
  const lastSavedDraftRef = useRef('');
  const formTopRef = useRef(null);
  const draftDocId = buildQuestionnaireDraftId(currentUser, currentMonth);
  const localDraftKey = `${DRAFT_STORAGE_PREFIX}${draftDocId}`;
  const legacyDraftKey = `${DRAFT_STORAGE_PREFIX}${currentUser.id}`;
  const localDraftKeys = useMemo(
    () => Array.from(new Set([localDraftKey, legacyDraftKey].filter(Boolean))),
    [localDraftKey, legacyDraftKey],
  );

  const dimensions = questionnaire.getDimensions();
  const steps = useMemo(
    () => [
      ...dimensions.map((dimension) => ({
        id: dimension.id,
        name: dimension.name,
        icon: dimensionIcons[dimension.id] || ChartBarIcon,
      })),
      { id: 'review', name: 'Revisión final', icon: CheckCircleIcon },
    ],
    [dimensions],
  );
  const isPreview = step === dimensions.length;
  const isEditing = !!existingSubmission;

  const readLocalDraft = useCallback(() => {
    for (const key of localDraftKeys) {
      const savedDraft = localStorage.getItem(key);
      if (savedDraft) {
        try {
          return { ...JSON.parse(savedDraft), storageKey: key };
        } catch (err) {
          console.warn('No se pudo parsear el borrador local', err);
        }
      }
    }
    return null;
  }, [localDraftKeys]);

  const removeLocalDraft = useCallback(() => {
    localDraftKeys.forEach((key) => localStorage.removeItem(key));
  }, [localDraftKeys]);

  const writeLocalDraft = useCallback((draft) => {
    localStorage.setItem(localDraftKey, JSON.stringify(draft));
    localDraftKeys.forEach((key) => {
      if (key !== localDraftKey) localStorage.removeItem(key);
    });
  }, [localDraftKey, localDraftKeys]);

  const buildDraftSnapshot = useCallback((clientUpdatedAt = new Date().toISOString()) => ({
    formData,
    step,
    month: currentMonth,
    companyId: currentUser.id,
    companyUid: currentUser.uid || currentUser.id,
    companyName: currentUser.name || '',
    clientUpdatedAt,
  }), [currentMonth, currentUser.id, currentUser.name, currentUser.uid, formData, step]);

  const getDraftSignature = useCallback((draft) => (
    JSON.stringify({
      formData: draft.formData || {},
      step: normalizeDraftStep(draft.step, dimensions.length),
      month: draft.month || currentMonth,
    })
  ), [currentMonth, dimensions.length]);

  const persistDraft = useCallback(async ({ notifySuccess = false } = {}) => {
    const draft = buildDraftSnapshot();
    const signature = getDraftSignature(draft);

    try {
      setDraftSaveState((prev) => ({ ...prev, status: 'saving' }));
      writeLocalDraft(draft);

      if (firebaseEnabled && currentUser.uid) {
        await saveQuestionnaireDraft({
          docId: draftDocId,
          companyUid: currentUser.uid,
          companyId: currentUser.id,
          companyName: currentUser.name || '',
          month: currentMonth,
          formData: draft.formData,
          step: draft.step,
          clientUpdatedAt: draft.clientUpdatedAt,
        });
      }

      lastSavedDraftRef.current = signature;
      setDraftSaveState({ status: 'saved', savedAt: draft.clientUpdatedAt });
      if (notifySuccess) onNotify('Borrador guardado correctamente.', 'success');
    } catch (err) {
      console.warn('No se pudo guardar el borrador', err);
      setDraftSaveState((prev) => ({ ...prev, status: 'error' }));
      if (notifySuccess) onNotify('No se pudo guardar el borrador. Revisa tu conexión o navegador.', 'error');
      throw err;
    }
  }, [buildDraftSnapshot, currentMonth, currentUser.id, currentUser.name, currentUser.uid, draftDocId, getDraftSignature, onNotify, writeLocalDraft]);

  useEffect(() => {
    let cancelled = false;

    const hydrateDraft = async () => {
      setDraftReady(false);
      lastSavedDraftRef.current = '';

      if (existingSubmission) {
        setFormData(existingSubmission.data || {});
        setStep(0);
        setErrors({});
        setDraftSaveState({ status: 'idle', savedAt: '' });
        setDraftReady(true);
        return;
      }

      if (canSubmit === false) {
        setFormData({});
        setStep(0);
        setErrors({});
        setDraftSaveState({ status: 'idle', savedAt: '' });
        setDraftReady(true);
        return;
      }

      let localDraft = null;
      try {
        localDraft = readLocalDraft();
      } catch (err) {
        console.warn('No se pudo leer el borrador local', err);
        onNotify('No pudimos leer el respaldo local de borrador en este navegador.', 'error');
      }

      let remoteDraft = null;
      if (firebaseEnabled && currentUser.uid) {
        try {
          remoteDraft = await getQuestionnaireDraft(draftDocId);
        } catch (err) {
          console.warn('No se pudo recuperar el borrador remoto', err);
          onNotify('No pudimos recuperar el borrador remoto. Usaremos el respaldo local si existe.', 'error');
        }
      }

      if (cancelled) return;

      const recoveredDraft = pickNewestDraft(remoteDraft, localDraft);
      if (recoveredDraft?.formData && Object.keys(recoveredDraft.formData).length > 0) {
        const recoveredStep = normalizeDraftStep(recoveredDraft.step, dimensions.length);
        const normalizedDraft = {
          ...recoveredDraft,
          step: recoveredStep,
          month: recoveredDraft.month || currentMonth,
        };
        setFormData(normalizedDraft.formData);
        setStep(recoveredStep);
        setErrors({});
        lastSavedDraftRef.current = getDraftSignature(normalizedDraft);
        setDraftSaveState({
          status: 'saved',
          savedAt: normalizedDraft.clientUpdatedAt || normalizedDraft.savedAt || normalizedDraft.updatedAt || '',
        });
        try {
          writeLocalDraft(normalizedDraft);
        } catch (err) {
          console.warn('No se pudo sincronizar el borrador local', err);
        }
        onNotify('Recuperamos tu último borrador guardado.', 'info');
      } else {
        setFormData({});
        setStep(0);
        setErrors({});
        setDraftSaveState({ status: 'idle', savedAt: '' });
      }

      setDraftReady(true);
    };

    hydrateDraft();
    return () => {
      cancelled = true;
    };
  }, [canSubmit, currentMonth, currentUser.uid, dimensions.length, draftDocId, existingSubmission, getDraftSignature, onNotify, readLocalDraft, writeLocalDraft]);

  const handleChange = (id, value) => {
    setFormData((prev) => {
      if (id === 'meta_naviera_principal') {
        const existingDiagnostics = normalizeDiagnosticList(prev.meta_naviera_detalle);
        const nextDiagnostics = existingDiagnostics.length
          ? existingDiagnostics.map((detail, index) => (
              index === 0 && (!detail.name || detail.name === prev.meta_naviera_principal)
                ? { ...detail, name: value }
                : detail
            ))
          : [createEmptyNavieraDiagnostic(value)];

        return { ...prev, [id]: value, meta_naviera_detalle: nextDiagnostics };
      }
      if (id === 'd4_q8' && value !== 'Otros') {
        return { ...prev, [id]: value, d4_q8_other: '' };
      }
      return { ...prev, [id]: value };
    });
    setErrors((prev) => ({ ...prev, [id]: undefined }));
  };

  useEffect(() => {
    if (!draftReady || (canSubmit === false && !isEditing)) return undefined;
    if (step === 0 && Object.keys(formData).length === 0) return undefined;

    const draft = buildDraftSnapshot();
    const signature = getDraftSignature(draft);
    if (signature === lastSavedDraftRef.current) return undefined;

    setDraftSaveState((prev) => ({ ...prev, status: 'pending' }));
    const timer = setTimeout(() => {
      persistDraft().catch(() => {});
    }, DRAFT_AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [buildDraftSnapshot, canSubmit, draftReady, formData, getDraftSignature, isEditing, persistDraft, step]);

  const clearDraft = async () => {
    try {
      removeLocalDraft();
    } catch (err) {
      console.warn('No se pudo eliminar el borrador local', err);
    }
    try {
      if (firebaseEnabled && currentUser.uid) {
        await deleteQuestionnaireDraft(draftDocId);
      }
    } catch (err) {
      console.warn('No se pudo eliminar el borrador remoto', err);
    }
    lastSavedDraftRef.current = '';
    setDraftSaveState({ status: 'idle', savedAt: '' });
  };

  const handleSaveDraft = async () => {
    await persistDraft({ notifySuccess: true }).catch(() => {});
  };

  const validateDimension = (dimension) => {
    const newErrors = {};
    dimension.questions.forEach((q) => {
      if (q.id === 'd4_q8_other' && formData.d4_q8 !== 'Otros') {
        return;
      }
      const required = q.required !== false;
      if (q.type === 'naviera_diagnostics') {
        const diagnosticsError = validateNavieraDiagnostics(formData[q.id], q.config);
        if (required && diagnosticsError) {
          newErrors[q.id] = diagnosticsError;
        }
        return;
      }
      if (required && (!formData[q.id] || formData[q.id] === '')) {
        newErrors[q.id] = 'Respuesta obligatoria';
      }
    });
    if (dimension.id === 'port' && formData.d4_q8 === 'Otros' && !formData.d4_q8_other?.trim()) {
      newErrors.d4_q8_other = 'Especifica el puerto utilizado';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return false;
    }
    return true;
  };

  const scrollToTop = useCallback(() => {
    if (formTopRef.current) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const nextStep = () => {
    const dimension = dimensions[step];
    if (dimension && !validateDimension(dimension)) {
      onNotify('Completa los campos obligatorios antes de continuar.', 'error');
      return;
    }
    if (step < dimensions.length) {
      setStep(step + 1);
      scrollToTop();
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
      scrollToTop();
    }
  };

  const handleSubmit = async () => {
    for (let i = 0; i < dimensions.length; i += 1) {
      const dim = dimensions[i];
      if (!validateDimension(dim)) {
        setStep(i);
        onNotify('Completa todos los campos obligatorios antes de enviar.', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      await onSubmit({ formData, editingSubmission: existingSubmission });
      await clearDraft();
      setFormData({});
      setErrors({});
      setStep(0);
      if (isEditing && onCancelEdit) onCancelEdit();
    } catch (error) {
      onNotify(error.message, 'error');
    }

    setIsLoading(false);
  };

  const renderStepContent = () => {
    if (isPreview) {
      return <PreviewStep formData={formData} />;
    }

    const dimension = dimensions[step];
    if (!dimension) {
      console.error('Error: Dimensión no encontrada en el paso', step);
      return <div>Error al cargar esta sección.</div>;
    }

    const StepIcon = dimensionIcons[dimension.id] || ChartBarIcon;
    const visibleQuestions = dimension.questions.filter((q) => !(q.id === 'd4_q8_other' && formData.d4_q8 !== 'Otros'));
    const answeredCount = visibleQuestions.filter((q) => formData[q.id] && formData[q.id] !== '').length;

    return (
      <div key={dimension.id} className="space-y-5">
        <div className="rounded-2xl bg-gradient-to-r from-brand-blue/[0.06] to-transparent border border-brand-blue/10 p-5">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-blue text-white flex-shrink-0">
              <StepIcon className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[0.6rem] uppercase text-slate-400">Dimensión {step + 1} de {dimensions.length}</p>
              <h2 className="text-xl font-bold font-jakarta text-slate-900 mt-0.5">{dimension.name}</h2>
              <p className="text-sm text-slate-500 mt-1">Responde cada pregunta con base en tu experiencia del último mes.</p>
            </div>
            <span className="text-xs font-semibold text-brand-blue bg-brand-blue/10 px-3 py-1.5 rounded-full flex-shrink-0 hidden sm:block">
              {answeredCount}/{visibleQuestions.length} respondidas
            </span>
          </div>
        </div>
        <div className="space-y-4">
          {dimension.questions.map((q) => {
            if (q.id === 'd4_q8_other' && formData.d4_q8 !== 'Otros') {
              return null;
            }
            const props = {
              question: q,
              value: formData[q.id],
              onChange: handleChange,
              error: errors[q.id],
            };
            switch (q.type) {
              case 'likert':
                return <LikertScale key={q.id} {...props} />;
              case 'binary':
                return <BinaryChoice key={q.id} {...props} />;
              case 'select':
                return <SelectChoice key={q.id} {...props} />;
              case 'open':
                return <OpenText key={q.id} {...props} />;
              case 'naviera_diagnostics':
                return <NavieraDiagnostics key={q.id} {...props} primaryCarrier={formData.meta_naviera_principal} />;
              default:
                return null;
            }
          })}
        </div>
      </div>
    );
  };

  let isPastDeadline = false;
  let deadlineString = 'No definida';

  if (deadline) {
    const [year, month, day] = deadline.split('-').map(Number);
    const deadlineDate = new Date(Date.UTC(year, month - 1, day, 23, 59, 59));
    isPastDeadline = new Date() > deadlineDate;
    deadlineString = deadlineDate.toLocaleDateString('es-ES', { timeZone: 'UTC' });
  }

  const submissionBlocked = (!canSubmit && !isEditing) || (deadline && isPastDeadline && !isEditing);
  const draftSavedTime = formatDraftSavedAt(draftSaveState.savedAt);
  const draftStatusText = {
    pending: 'Autoguardado pendiente',
    saving: 'Guardando borrador...',
    saved: draftSavedTime ? `Borrador guardado ${draftSavedTime}` : 'Borrador guardado',
    error: 'No se pudo autoguardar el borrador',
    idle: '',
  }[draftSaveState.status] || '';
  const draftStatusClass = draftSaveState.status === 'error' ? 'text-rose-500' : 'text-slate-400';

  const progressPercent = ((step + 1) / steps.length) * 100;

  return (
    <div className="space-y-5 pt-4" ref={formTopRef}>
      {/* Progress + Stepper Card */}
      <div className="card-premium overflow-hidden">
        {/* Continuous progress bar */}
        <div className="h-1.5 bg-slate-100">
          <div
            className="h-full bg-gradient-to-r from-brand-blue to-brand-bright rounded-r-full"
            style={{ width: `${progressPercent}%`, transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
          />
        </div>
        <div className="p-4 space-y-4 md:p-5">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[0.6rem] uppercase text-slate-400">Fase actual</p>
              <h3 className="text-lg font-bold font-jakarta text-slate-900">
                {isPreview ? 'Revisión y envío' : dimensions[step]?.name || 'Dimensión'}
              </h3>
              <p className="text-sm text-slate-500">Plazo: {deadlineString}{isPastDeadline && ' · vencido'}</p>
            </div>
            <span className="text-sm font-bold font-jakarta text-brand-blue bg-brand-blue/10 rounded-full px-3.5 py-1.5">
              {Math.min(step + 1, steps.length)} / {steps.length}
            </span>
          </div>
          {/* Compact circle stepper — hidden on small screens */}
          <div className="hidden md:flex items-center gap-0 overflow-x-auto pb-1">
            {steps.map((item, index) => {
              const isActive = index === step;
              const isCompleted = index < step;
              return (
                <React.Fragment key={item.id}>
                  {/* Circle */}
                  <div className="flex flex-col items-center gap-1.5 flex-shrink-0" style={{ minWidth: 72 }}>
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                        isActive
                          ? 'bg-brand-blue text-white shadow-card ring-4 ring-brand-blue/20'
                          : isCompleted
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-100 text-slate-400 border border-slate-200'
                      }`}
                    >
                      {isCompleted ? '✓' : index + 1}
                    </span>
                    <p className={`text-[0.6rem] font-semibold text-center leading-tight max-w-[72px] ${
                      isActive ? 'text-brand-blue' : isCompleted ? 'text-emerald-600' : 'text-slate-400'
                    }`}>{item.name}</p>
                  </div>
                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-px min-w-[16px] mx-1" style={{ background: index < step ? '#10B981' : '#E2E8F0' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {/* Mobile: just show step dots */}
          <div className="flex md:hidden items-center justify-center gap-2">
            {steps.map((_, index) => (
              <span
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === step ? 'w-6 bg-brand-blue' : index < step ? 'w-2 bg-emerald-400' : 'w-2 bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {submissionBlocked && !isEditing && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 text-sm text-amber-800">
          Ya existe un envío en revisión o aprobado para este mes. Revisa tu historial antes de continuar.
        </div>
      )}

      {/* Form content */}
      <div className="card-premium p-4 md:p-6">{renderStepContent()}</div>

      {/* Navigation */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex gap-3">
            <button
              onClick={prevStep}
              disabled={step === 0}
              className="flex items-center rounded-xl border border-slate-200/70 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 hover:shadow-sm transition-all duration-200"
            >
              <ChevronLeftIcon className="mr-2 h-4 w-4" />
              Anterior
            </button>
            <button
              onClick={handleSaveDraft}
              disabled={submissionBlocked || draftSaveState.status === 'saving'}
              className="rounded-xl border border-slate-200/70 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 hover:shadow-sm transition-all duration-200"
            >
              Guardar borrador
            </button>
          </div>
          {draftStatusText && (
            <p className={`text-xs ${draftStatusClass}`} aria-live="polite">
              {draftStatusText}
            </p>
          )}
        </div>
        {isPreview ? (
          <button
            onClick={handleSubmit}
            disabled={isLoading || submissionBlocked}
            className={`rounded-xl px-7 py-3 text-sm font-bold text-white shadow-card transition-all duration-300 ${
              isLoading
                ? 'bg-emerald-400'
                : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:shadow-card-hover hover:-translate-y-0.5'
            } disabled:opacity-40`}
          >
            {isLoading ? 'Enviando…' : '✓  Enviar cuestionario'}
          </button>
        ) : (
          <button
            onClick={nextStep}
            disabled={submissionBlocked}
            className={`flex items-center rounded-xl px-7 py-3 text-sm font-bold text-white shadow-card transition-all duration-300 ${
              submissionBlocked
                ? 'bg-slate-400'
                : 'bg-gradient-to-r from-brand-blue to-brand-bright hover:shadow-card-hover hover:-translate-y-0.5'
            }`}
          >
            Continuar
            <ChevronRightIcon className="ml-2 h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuestionnaireStepper;
